/**
 * PDF Generation Constants
 * Centralized configuration for report generation
 */

export const MAX_REPORT_MEASURE_DISTANCE_KM = 30;

// Decimal places for the marking's reference coordinates (~11m precision).
export const COORDINATE_DECIMAL_PLACES = 4;

// Below this threshold, the nearest infrastructure is considered to be inside
// the selected geometry (município/área) rather than "near" it — the backend's
// spatial query can return a tiny non-zero distance for touching/overlapping geometries.
export const INFRASTRUCTURE_INSIDE_SELECTION_THRESHOLD_KM = 0.05;

export const PDF_CONSTANTS = {
  // Image capture and compression
  MAX_PAYLOAD_SIZE: 10 * 1024 * 1024, // 10MB
  COMPRESSION_QUALITY: 0.7,
  COMPRESSION_MAX_WIDTH: 1920,
  COMPRESSION_MAX_HEIGHT: 1080,

  // Capture dimensions
  MAP_CAPTURE_WIDTH: 1920,
  MAP_CAPTURE_HEIGHT: 1080,
  CHART_CAPTURE_SCALE: 2,

  // Timeouts (in milliseconds)
  IMAGE_LOAD_TIMEOUT: 10000, // 10 seconds
  MAP_TILES_WAIT: 2000, // 2 seconds for tiles to load
  CAPTURE_DELAY: 500, // Delay before capture

  // API
  API_TIMEOUT: 30000, // 30 seconds
} as const;

export const PDF_LAYOUT = {
  // Page setup
  PAGE_FORMAT: 'a4' as const,
  PAGE_ORIENTATION: 'portrait' as const,

  // Margins (in mm)
  MARGIN_TOP: 20,
  MARGIN_RIGHT: 20,
  MARGIN_BOTTOM: 20,
  MARGIN_LEFT: 20,

  // Font sizes (in pt)
  FONT_SIZE_TITLE: 22,
  FONT_SIZE_HEADING: 16,
  FONT_SIZE_SUBHEADING: 14,
  FONT_SIZE_BODY: 11,
  FONT_SIZE_SMALL: 9,
  FONT_SIZE_METADATA: 8,

  // Spacing (in mm)
  SECTION_SPACING: 10,
  PARAGRAPH_SPACING: 5,
  LINE_HEIGHT: 1.5,

  // Legend layout
  LEGEND_COLUMNS: 2,
  LEGEND_COLUMN_GAP: 10,
  LEGEND_ITEM_HEIGHT: 8,
  LEGEND_SYMBOL_SIZE: 5,
  LEGEND_SYMBOL_MARGIN: 2,
  LEGEND_TEXT_OFFSET: 8,

  // Image sizing
  IMAGE_MAX_WIDTH: 170, // A4 width minus margins
  IMAGE_MAX_HEIGHT: 120,
} as const;

export const PROGRESS_STEPS = {
  IDLE: {
    step: 'idle',
    message: '',
    percentage: 0,
  },
  CAPTURING: {
    step: 'capturing',
    message: 'Capturando elementos visuais...',
    percentage: 20,
  },
  COMPRESSING: {
    step: 'compressing',
    message: 'Comprimindo imagens...',
    percentage: 40,
  },
  UPLOADING: {
    step: 'uploading',
    message: 'Enviando dados para o servidor...',
    percentage: 60,
  },
  GENERATING: {
    step: 'generating',
    message: 'Gerando PDF...',
    percentage: 80,
  },
  COMPLETE: {
    step: 'complete',
    message: 'Relatório gerado com sucesso!',
    percentage: 100,
  },
} as const;

export const UI_ELEMENTS_TO_HIDE = [
  // Leaflet control containers (all controls live inside these)
  '.leaflet-control-container',
  '.leaflet-top',
  '.leaflet-bottom',
  // Individual Leaflet built-in controls
  '.leaflet-control',
  '.leaflet-control-zoom',
  '.leaflet-control-attribution',
  '.leaflet-control-scale',
  // Leaflet Draw plugin
  '.leaflet-draw',
  '.leaflet-draw-toolbar',
  '.leaflet-draw-section',
  '.leaflet-draw-actions',
  // App-level overlays (by ID — both MapActionBar and LegendActionBar use id="mapActionBar")
  '#mapActionBar',
  // App-level overlays (by z-action-bar utility class — GeocodingSearchBar etc.)
  '[class*="z-action-bar"]',
  // Floating map controls that must not appear in the captured report map image
  '.fernando-noronha-floating-container',
  '.dimension-control-panel',
  // Leaflet popups and tooltips — must be hidden so open popups/tooltips
  // (from GeocodingSearchBar marker, WFS layer clicks, etc.) do not appear in report images
  '.leaflet-popup',
  '.leaflet-tooltip',
] as const;

export const ERROR_MESSAGES = {
  // Validation errors
  NO_OPTIONS_SELECTED: 'Por favor, selecione pelo menos uma opção de conteúdo (Mapa, Estatísticas ou Legendas).',
  NO_LAYERS_SELECTED: 'Por favor, selecione pelo menos um grupo de camadas para incluir no relatório.',
  NO_STATISTICS_DATA: 'Não há dados de estatísticas disponíveis. Certifique-se de que há informações no gráfico.',

  // Capture errors
  MAP_CAPTURE_FAILED: 'Falha ao capturar o mapa. Por favor, tente novamente.',
  CHART_CAPTURE_FAILED: 'Falha ao capturar o gráfico de estatísticas. Por favor, tente novamente.',
  LEGEND_CAPTURE_FAILED: 'Falha ao processar as legendas. Por favor, tente novamente.',

  // Compression errors
  COMPRESSION_FAILED: 'Falha ao comprimir as imagens. Por favor, tente novamente.',
  IMAGE_TOO_LARGE: 'O tamanho das imagens excede o limite permitido. Tente desabilitar algumas opções.',

  // API errors
  NETWORK_ERROR: 'Erro de conexão com o servidor. Verificando se é possível gerar o PDF localmente...',
  API_ERROR: 'Erro ao comunicar com o servidor. Tentando gerar PDF localmente...',
  TIMEOUT_ERROR: 'A requisição demorou muito tempo. Tentando gerar PDF localmente...',

  // Generation errors
  PDF_GENERATION_FAILED: 'Falha ao gerar o PDF. Por favor, tente novamente.',
  UNKNOWN_ERROR: 'Ocorreu um erro inesperado. Por favor, tente novamente.',
} as const;

export const LAYER_CATEGORIES = {
  INFRAESTRUTURA: 'Infraestrutura',
  MEIO_AMBIENTE: 'Meio Ambiente',
  SOCIAL: 'Social',
  PLANEJAMENTO: 'Planejamento',
  OUTROS: 'Outros',
} as const;

export const DASH_ARRAY_PATTERNS = {
  SOLID: '',
  DASHED: '5,5',
  DOTTED: '2,2',
  DASH_DOT: '5,2,2,2',
} as const;
